import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import DOMPurify from 'isomorphic-dompurify';
import * as Styled from './MTimer.styles';
import { MTimerAlignment, MTimerProps, TimeLeft } from './MTimer.types';
import { ADigit, ADigitSize } from '../../atoms/ADigit';
import { ABody, ABodySize } from '../../atoms/ABody';

const COMPONENT_NAME = 'm-web-ui-kit-MTimer';
const TEST_ID = 'web-ui-kit-MTimer';

const getAccessibleText = (html: string | undefined): string => {
  if (!html) return '';

  const cleanEl = DOMPurify.sanitize(html, {
    RETURN_DOM: true,
  }) as unknown as HTMLElement;

  const deletedElements = cleanEl.querySelectorAll('del, strike, s');
  deletedElements.forEach(el => el.remove());

  return (cleanEl.textContent || '').trim().replace(/\s+/g, ' ');
};

const calculateTimeLeft = (targetDate: string): TimeLeft => {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
};

const formatNumber = (num: number): string => String(num).padStart(2, '0');

export const MTimer: FC<MTimerProps> = ({
  alignment = MTimerAlignment.Centered,
  aDigit,
  aBody,
  className,
  targetDate,
  daysLabel = 'DAYS',
  hoursLabel = 'HOURS',
  minutesLabel = 'MINUTES',
  secondsLabel = 'SECONDS',
  updateInterval = 1000,
  screenReaderInterval = 300000,
  onComplete,
  isAuthorMode = false,
  isLoading = false,
  bodyText,
  ...rest
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    targetDate ? calculateTimeLeft(targetDate) : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );
  const [announcementText, setAnnouncementText] = useState('');
  const [isClient, setIsClient] = useState(false);

  const lastAnnouncementRef = useRef(Date.now());
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const updateTime = useCallback(() => {
    if (!targetDate) return;

    const newTimeLeft = calculateTimeLeft(targetDate);
    setTimeLeft(newTimeLeft);

    if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
      if (onCompleteRef.current && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onCompleteRef.current();
      }
    }

    const now = Date.now();
    const shouldAnnounce = now - lastAnnouncementRef.current >= screenReaderInterval;

    if (shouldAnnounce) {
      const timeLeftText = `${newTimeLeft.days} days, ${newTimeLeft.hours} hours, ${newTimeLeft.minutes} minutes remaining`;
      const accessibleBodyText = getAccessibleText(bodyText);
      const announcement = accessibleBodyText ? `${accessibleBodyText}. ${timeLeftText}` : timeLeftText;

      setAnnouncementText(announcement);
      lastAnnouncementRef.current = now;
    }
  }, [bodyText, screenReaderInterval, targetDate]);

  useEffect(() => {
    if (!isClient || isAuthorMode || !targetDate) {
      return;
    }

    hasCompletedRef.current = false;
    updateTime();

    const interval = setInterval(updateTime, updateInterval);

    return () => clearInterval(interval);
  }, [isAuthorMode, isClient, targetDate, updateInterval, updateTime]);

  if (!targetDate) {
    const alignmentClass = alignment === MTimerAlignment.Centered ? 'align-items-center' : 'align-items-start';

    return (
      <Styled.Wrapper
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        $alignment={alignment}
        className={clsx('d-flex', 'flex-column', alignmentClass, className)}
        {...rest}
      >
        {aDigit && (
          <ADigit {...aDigit} size={aDigit.size ?? ADigitSize.Large} className={clsx('pb-0', aDigit.className)} />
        )}
        {aBody?.bodyText ? <ABody {...aBody} /> : null}
      </Styled.Wrapper>
    );
  }

  if (!isClient || isLoading) {
    return (
      <Styled.SkeletonWrapper
        data-component-name={COMPONENT_NAME}
        data-testid={`${TEST_ID}-skeleton`}
        className={className}
        {...rest}
      >
        <Styled.ScreenReaderOnly>Loading countdown timer</Styled.ScreenReaderOnly>
        <Styled.TimerDisplay aria-hidden="true">
          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>
        </Styled.TimerDisplay>
      </Styled.SkeletonWrapper>
    );
  }

  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={className} {...rest}>
      <Styled.TimerDisplay aria-hidden="true">
        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.days)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={daysLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.hours)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={hoursLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.minutes)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={minutesLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.seconds)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={secondsLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>
      </Styled.TimerDisplay>

      <Styled.ScreenReaderOnly>
        {`${timeLeft.days} days, ${timeLeft.hours} hours, ${timeLeft.minutes} minutes remaining.`}
      </Styled.ScreenReaderOnly>

      <Styled.ScreenReaderOnly aria-live="polite" aria-atomic="true">
        {announcementText}
      </Styled.ScreenReaderOnly>
    </Styled.Wrapper>
  );
};

export { MTimerAlignment, TEST_ID as M_TIMER_TEST_ID };
